import { LoaderCircle, Mic, RotateCcw, Square, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { transcribeSpeech } from '../../app/speech'
import { t } from '../../copy'

type Phase =
  | { kind: 'idle' }
  | { kind: 'recording'; elapsedMS: number }
  | { kind: 'transcribing' }
  | { kind: 'failed'; blob: Blob; mimeType: string; durationMS: number }
  | { kind: 'no-mic' }

export function VoiceInput({ onTranscript }: { readonly onTranscript: (text: string) => void }) {
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const startedAtRef = useRef(0)
  const timerRef = useRef<number | null>(null)
  const discardRef = useRef(false)

  const supported =
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    !!window.navigator?.mediaDevices?.getUserMedia

  function stopTracks() {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    if (timerRef.current !== null) window.clearInterval(timerRef.current)
    timerRef.current = null
  }

  useEffect(
    () => () => {
      discardRef.current = true
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
      stopTracks()
    },
    [],
  )

  async function start() {
    if (!supported) return
    discardRef.current = false
    try {
      const stream = await window.navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = preferredMimeType()
      const recorder = mimeType
        ? new window.MediaRecorder(stream, { mimeType })
        : new window.MediaRecorder(stream)
      chunksRef.current = []
      streamRef.current = stream
      recorderRef.current = recorder
      startedAtRef.current = Date.now()
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data)
      }
      recorder.onstop = () => {
        const durationMS = Math.max(1, Date.now() - startedAtRef.current)
        const resolvedMime = recorder.mimeType || mimeType || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type: resolvedMime })
        recorderRef.current = null
        stopTracks()
        if (discardRef.current) {
          setPhase({ kind: 'idle' })
          return
        }
        void submit(blob, resolvedMime, durationMS)
      }
      recorder.start(250)
      setPhase({ kind: 'recording', elapsedMS: 0 })
      timerRef.current = window.setInterval(() => {
        setPhase({ kind: 'recording', elapsedMS: Date.now() - startedAtRef.current })
      }, 250)
    } catch {
      stopTracks()
      setPhase({ kind: 'no-mic' })
    }
  }

  async function submit(blob: Blob, mimeType: string, durationMS: number) {
    if (blob.size === 0) {
      setPhase({ kind: 'failed', blob, mimeType, durationMS })
      return
    }
    setPhase({ kind: 'transcribing' })
    try {
      const result = await transcribeSpeech({ blob, mimeType, durationMS })
      onTranscript(result.text)
      setPhase({ kind: 'idle' })
    } catch {
      setPhase({ kind: 'failed', blob, mimeType, durationMS })
    }
  }

  function stop(discard: boolean) {
    discardRef.current = discard
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
  }

  if (!supported) {
    return (
      <button
        type="button"
        disabled
        data-testid="lulu-voice-unsupported"
        aria-label={t('lulu_workbench.voice.unsupported')}
        title={t('lulu_workbench.voice.unsupported')}
        className="grid size-8 shrink-0 place-items-center rounded-full text-npc-lulu-t4 opacity-50"
      >
        <Mic size={17} aria-hidden />
      </button>
    )
  }

  if (phase.kind === 'recording') {
    return (
      <span className="flex shrink-0 items-center gap-1" data-testid="lulu-voice-recording">
        <span className="min-w-[34px] text-right font-npc-mono text-npc-meta text-npc-lulu-bad">
          {clock(phase.elapsedMS)}
        </span>
        <button
          type="button"
          onClick={() => stop(false)}
          aria-label={t('lulu_workbench.voice.stop')}
          title={t('lulu_workbench.voice.stop')}
          className="grid size-8 place-items-center rounded-full bg-npc-lulu-bad text-white"
          data-testid="lulu-voice-stop"
        >
          <Square size={13} fill="currentColor" aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => stop(true)}
          aria-label={t('lulu_workbench.voice.cancel')}
          title={t('lulu_workbench.voice.cancel')}
          className="grid size-8 place-items-center rounded-full text-npc-lulu-t3"
          data-testid="lulu-voice-cancel"
        >
          <X size={17} aria-hidden />
        </button>
      </span>
    )
  }

  if (phase.kind === 'transcribing') {
    return (
      <button
        type="button"
        disabled
        data-testid="lulu-voice-transcribing"
        aria-label={t('lulu_workbench.voice.transcribing')}
        title={t('lulu_workbench.voice.transcribing')}
        className="grid size-8 shrink-0 place-items-center rounded-full text-npc-lulu-t3"
      >
        <LoaderCircle size={17} className="animate-spin" aria-hidden />
      </button>
    )
  }

  if (phase.kind === 'failed') {
    return (
      <button
        type="button"
        onClick={() => void submit(phase.blob, phase.mimeType, phase.durationMS)}
        data-testid="lulu-voice-retry"
        aria-label={t('lulu_workbench.voice.retry')}
        title={t('lulu_workbench.voice.retry')}
        className="grid size-8 shrink-0 place-items-center rounded-full text-npc-lulu-warn"
      >
        <RotateCcw size={17} aria-hidden />
      </button>
    )
  }

  if (phase.kind === 'no-mic') {
    return (
      <button
        type="button"
        onClick={() => void start()}
        data-testid="lulu-voice-no-mic"
        aria-label={t('lulu_workbench.voice.no_mic')}
        title={t('lulu_workbench.voice.no_mic')}
        className="grid size-8 shrink-0 place-items-center rounded-full text-npc-lulu-warn"
      >
        <Mic size={17} aria-hidden />
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={() => void start()}
      data-testid="lulu-voice-start"
      aria-label={t('lulu_workbench.voice.start')}
      title={t('lulu_workbench.voice.start')}
      className="grid size-8 shrink-0 place-items-center rounded-full text-npc-lulu-t3 hover:bg-npc-lulu-bg-2 hover:text-npc-lulu-t1"
    >
      <Mic size={17} aria-hidden />
    </button>
  )
}

function preferredMimeType(): string {
  for (const type of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']) {
    if (window.MediaRecorder.isTypeSupported(type)) return type
  }
  return ''
}

function clock(ms: number): string {
  const seconds = Math.floor(ms / 1000)
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}
